import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, getDocs } from "@firebase/firestore";
import { app } from '../../firebase.js';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Table from 'react-bootstrap/Table';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';

function TrendsOverTime() {
  const db = getFirestore(app);
  const [monthlyData, setMonthlyData] = useState([]);
  const [yearlyData, setYearlyData] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [yearlyStats, setYearlyStats] = useState({});
  const [selectedYear, setSelectedYear] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      const q = query(collection(db, 'users'));
      const querySnapshot = await getDocs(q);

      const monthlyCounts = {};
      const yearlyCounts = {};
      const allSources = new Set();

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const ts = data.appDate;
        const createdAt = ts?.toDate ? ts.toDate() : null;
        const source = data.source || 'Unknown';
        const status = data.userStatus || 'Unknown';

        if (createdAt) {
          allSources.add(source);

          const monthYear = `${createdAt.getMonth() + 1}-${createdAt.getFullYear()}`;
          const yearKey = `${createdAt.getFullYear()}`;

          // Monthly init
          if (!monthlyCounts[monthYear]) monthlyCounts[monthYear] = {};
          if (!monthlyCounts[monthYear][source]) {
            monthlyCounts[monthYear][source] = { converted: 0, notConverted: 0 };
          }

          // Yearly init
          if (!yearlyCounts[yearKey]) yearlyCounts[yearKey] = {};
          if (!yearlyCounts[yearKey][source]) {
            yearlyCounts[yearKey][source] = { converted: 0, notConverted: 0 };
          }

          // Status grouping
          if (['Student', 'Completed', 'Deferred', 'Expelled', 'Dropped'].includes(status)) {
            monthlyCounts[monthYear][source].converted += 1;
            yearlyCounts[yearKey][source].converted += 1;
          } else if (['inactive', 'Did Not Join'].includes(status)) {
            monthlyCounts[monthYear][source].notConverted += 1;
            yearlyCounts[yearKey][source].notConverted += 1;
          }
        }
      });

      // Monthly formatted
      const monthlyFormatted = Object.keys(monthlyCounts).map((key) => {
        const [month, year] = key.split('-').map(Number);
        return {
          month: key,
          year,
          monthNum: month,
          ...Object.fromEntries(
            Object.entries(monthlyCounts[key]).map(([src, vals]) => [
              src,
              vals
            ])
          ),
        };
      }).sort((a, b) => {
        const dateA = new Date(a.year, a.monthNum - 1);
        const dateB = new Date(b.year, b.monthNum - 1);
        return dateB - dateA;
      });

      // Yearly formatted
      const yearlyFormatted = Object.keys(yearlyCounts).map((key) => ({
        year: key,
        ...Object.fromEntries(
          Object.entries(yearlyCounts[key]).map(([src, vals]) => [
            src,
            vals
          ])
        ),
      })).sort((a, b) => b.year - a.year);

      // Compute stats
      const stats = {};
      monthlyFormatted.forEach((row) => {
        const year = row.year;
        if (!stats[year]) stats[year] = { months: {} };

        let totalConverted = 0;
        let totalNotConverted = 0;

        Object.values(row).forEach((val) => {
          if (val?.converted !== undefined) {
            totalConverted += val.converted;
            totalNotConverted += val.notConverted;
          }
        });

        stats[year].months[row.month] = {
          converted: totalConverted,
          notConverted: totalNotConverted,
        };
      });

      Object.keys(stats).forEach((yr) => {
        const months = stats[yr].months;
        let best = null, worst = null, mostNC = null;

        Object.entries(months).forEach(([m, vals]) => {
          if (!best || vals.converted > months[best].converted) best = m;
          if (!mostNC || vals.notConverted > months[mostNC].notConverted) mostNC = m;
          if (!worst || vals.converted < months[worst].converted) worst = m;
        });

        stats[yr].bestMonth = { name: best, ...months[best] };
        stats[yr].worstMonth = { name: worst, ...months[worst] };
        stats[yr].mostNotConverted = { name: mostNC, ...months[mostNC] };
      });

      setMonthlyData(monthlyFormatted);
      setYearlyData(yearlyFormatted);
      setSources([...allSources]);
      setYearlyStats(stats);
      setLoading(false);
    };

    fetchData();
  }, [db]);

  return (
    <div className='mainContainer'>
      <LeftBar />
      <div className='theMainContainer'>
        <Dashnav />
        <div className="container mt-4">
          <h4>Trends Over Time (Converted vs Not Converted by Source)</h4>

          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              {/* Yearly Line Chart */}
              <div style={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <LineChart data={yearlyData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    {sources.map((src, i) => (
                      <React.Fragment key={src}>
                        <Line
                          key={`${src}-converted`}
                          type="monotone"
                          dataKey={`${src}.converted`}
                          stroke={`hsl(${i * 60},70%,50%)`}
                          name={`${src} - Converted`}
                        />
                        <Line
                          key={`${src}-notConverted`}
                          type="monotone"
                          dataKey={`${src}.notConverted`}
                          stroke={`hsl(${i * 60},70%,70%)`}
                          strokeDasharray="4 4"
                          name={`${src} - Not Converted`}
                        />
                      </React.Fragment>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
<br/><br/>
              {/* Year Filter */}
              <p>View Best and worst performing months accross the Years</p>
              <select
                className="form-select mt-3"
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
              >
                <option value="">Select Year</option>
                {Object.keys(yearlyStats).map((yr) => (
                  <option key={yr} value={yr}>{yr}</option>
                ))}
              </select>
              
              {/* Stats Display */}
              {selectedYear && yearlyStats[selectedYear] && (
                <div className="mt-3 alert alert-info">
                  <h6>Year {selectedYear} Performance</h6>
                  <p>
                    Best month (Conversions): {yearlyStats[selectedYear].bestMonth.name} 
                    ({yearlyStats[selectedYear].bestMonth.converted} converted)
                  </p>
                  <p>
                    Worst month (Conversions): {yearlyStats[selectedYear].worstMonth.name} 
                    ({yearlyStats[selectedYear].worstMonth.converted} converted)
                  </p>
                  <p>
                    Month with most not converted: {yearlyStats[selectedYear].mostNotConverted.name} 
                    ({yearlyStats[selectedYear].mostNotConverted.notConverted} not converted)
                  </p>
                </div>
              )}

              <br/><br/><br/>

                <h5>Students Conversion Distribution accross the years</h5>

              {/* Monthly Table */}
              <Table striped bordered hover className="mt-4">
                <thead>
                  <tr>
                    <th>Month-Year</th>
                    {sources.map((src) => (
                      <React.Fragment key={src}>
                        <th>{src} - Converted</th>
                        <th>{src} - Not Converted</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {monthlyData.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.month}</td>
                      {sources.map((src) => (
                        <React.Fragment key={src}>
                          <td>{row[src]?.converted || 0}</td>
                          <td>{row[src]?.notConverted || 0}</td>
                        </React.Fragment>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default TrendsOverTime;